import { useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Plus, Trash2 } from "lucide-react";
import { useWhatsappModelos, WhatsappModelo } from "@/hooks/useWhatsappModelos";

export default function WhatsappModelos() {
  const { modelos, createModelo, updateModelo, deleteModelo, seedDefaults } = useWhatsappModelos();
  const [editing, setEditing] = useState<WhatsappModelo | null>(null);
  const [form, setForm] = useState({ nome: "", tipo: "Geral", mensagem: "", ativo: true });

  const save = async () => {
    if (!form.nome || !form.mensagem) return;
    const saved = editing ? await updateModelo(editing.id, form) : await createModelo(form);
    if (saved) {
      setEditing(null);
      setForm({ nome: "", tipo: "Geral", mensagem: "", ativo: true });
    }
  };

  const edit = (modelo: WhatsappModelo) => {
    setEditing(modelo);
    setForm({ nome: modelo.nome, tipo: modelo.tipo, mensagem: modelo.mensagem, ativo: modelo.ativo });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><MessageCircle className="h-7 w-7" /> Modelos WhatsApp</h1>
            <p className="text-muted-foreground">Mensagens prontas com variáveis como {"{paciente}"}, {"{data}"}, {"{valor}"} e {"{clinica}"}.</p>
          </div>
          <Button variant="outline" onClick={seedDefaults}>Criar modelos padrão</Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{editing ? "Editar modelo" : "Novo modelo"}</CardTitle>
            <CardDescription>Use estes modelos em agendamentos, cobranças, orçamentos e retornos.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2 md:col-span-2">
                <Label>Nome</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mensagem</Label>
              <Textarea className="min-h-[120px]" value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm({ nome: "", tipo: "Geral", mensagem: "", ativo: true }); }}>Cancelar</Button>}
              <Button onClick={save}><Plus className="mr-2 h-4 w-4" />Salvar</Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          {modelos.map((modelo) => (
            <Card key={modelo.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{modelo.nome}</CardTitle>
                    <CardDescription><Badge variant="secondary">{modelo.tipo}</Badge></CardDescription>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteModelo(modelo.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="whitespace-pre-wrap text-sm">{modelo.mensagem}</p>
                <Button variant="outline" size="sm" onClick={() => edit(modelo)}>Editar</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
